import '@xyflow/react/dist/style.css';
import React from "react";
import {observer} from "mobx-react-lite";
import {useStores} from "~/stores";
import type {NodeMouseHandler, NodeTypes} from "@xyflow/react";
import {Background, BackgroundVariant, Controls, getNodesBounds, MiniMap, Panel, ReactFlow, useReactFlow} from "@xyflow/react";
import {Box, Stack, useTheme} from "@mui/material";
import {SegmentedControl} from "~/components/segmented-control/SegmentedControl";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import type {TaxonomyCanvasNode} from "~/prototypes/pages/taxonomy-canvas/taxonomyCanvasLayout.ts";
import {layoutTaxonomy, NODE_HEIGHT, NODE_WIDTH} from "~/prototypes/pages/taxonomy-canvas/taxonomyCanvasLayout.ts";
import {TaxonomyCanvasNodeView} from "~/prototypes/pages/taxonomy-canvas/TaxonomyCanvasNodeView.tsx";
import {mockTaxonomySpend} from "~/prototypes/pages/taxonomy-canvas/mockTaxonomySpend.ts";
import {spendColor} from "~/prototypes/pages/taxonomy-canvas/spendColor.ts";
import {SpendLegend} from "~/prototypes/pages/taxonomy-canvas/SpendLegend.tsx";
import type {LevelSelection} from "~/prototypes/pages/taxonomy-canvas/taxonomyIndex.ts";
import {deepestSelected, indexTaxonomy} from "~/prototypes/pages/taxonomy-canvas/taxonomyIndex.ts";
import {TaxonomyLevelFilter} from "~/prototypes/pages/taxonomy-canvas/TaxonomyLevelFilter.tsx";
import {ClickToEdit} from "~/components/ClickToEdit/ClickToEdit.tsx";
import type {TaxonomyEdit} from "~/prototypes/pages/taxonomy-canvas/taxonomyEdits.ts";
import {applyEdits} from "~/prototypes/pages/taxonomy-canvas/taxonomyEdits.ts";
import {taxonomyCanvasEditsStore} from "~/prototypes/pages/taxonomy-canvas/taxonomyCanvasEditsStore.ts";
import type {DeleteCategoryRequest} from "~/prototypes/pages/taxonomy-canvas/DeleteCategoryDialog.tsx";
import {DeleteCategoryDialog} from "~/prototypes/pages/taxonomy-canvas/DeleteCategoryDialog.tsx";
import type {DropRequest} from "~/prototypes/pages/taxonomy-canvas/useTaxonomyDrag.ts";
import {useTaxonomyDrag} from "~/prototypes/pages/taxonomy-canvas/useTaxonomyDrag.ts";
import {TaxonomyDropDialog} from "~/prototypes/pages/taxonomy-canvas/TaxonomyDropDialog.tsx";
import {HoveredNodePanel} from "~/prototypes/pages/taxonomy-canvas/HoveredNodePanel.tsx";
import {TaxonomyTreemap} from "~/prototypes/pages/taxonomy-canvas/TaxonomyTreemap.tsx";

const NODE_TYPES: NodeTypes = {taxonomy: TaxonomyCanvasNodeView};

type CanvasView = 'tree' | 'treemap';
const VIEWS: {key: CanvasView, label: string}[] = [{key: 'tree', label: 'Tree'}, {key: 'treemap', label: 'Treemap'}];

type Props = {
    /** The taxonomy's id (not the root node's id): edits are stored per taxonomy */
    taxonomyId: number
    tree: m_taxonomy.Tree
    name: string
}
/**
 * Figma-like canvas of the taxonomy: pan by dragging the background, zoom with the wheel or controls.
 * Click a category to expand or collapse its children. Nodes are shaded by (mock) spend: white = least, dark blue = most.
 * A Tree / Treemap switch shows the same (edited) taxonomy as an ECharts treemap instead.
 * The level filters on top expand the tree down to the chosen category and zoom to it.
 * The taxonomy name and every category can be renamed, and categories deleted (both after confirmation).
 * Drag a category onto one on the same level to merge it, or onto one a level up to move it (after confirmation).
 * All edits go to `taxonomyCanvasEditsStore` (in memory, shared with the approval page) — nothing is saved.
 * Must be rendered inside a `ReactFlowProvider`.
 */
export const TaxonomyCanvas: React.FC<Props> = observer(({taxonomyId, tree: sourceTree, name: initialName}) => {
    const theme = useTheme();
    // PROTOTYPE: all edits (approved + pending) are replayed on the API tree; nothing is saved
    const {approved, pending} = taxonomyCanvasEditsStore.get(taxonomyId);
    const {authStore} = useStores();
    // The signed-in user is recorded as the author of every edit
    const addEdit = React.useCallback(
        (edit: TaxonomyEdit) => taxonomyCanvasEditsStore.add(taxonomyId, edit, {
            id: authStore.userId,
            name: authStore.displayUser || authStore.email || 'Unknown user',
        }),
        [taxonomyId, authStore]
    );
    const [pendingDelete, setPendingDelete] = React.useState<DeleteCategoryRequest | null>(null);
    const [pendingDrop, setPendingDrop] = React.useState<DropRequest | null>(null);
    const tree = React.useMemo(
        () => applyEdits(sourceTree, [...approved, ...pending]),
        [sourceTree, approved, pending]
    );
    // The root node is the taxonomy itself: its label from the API is not the taxonomy name
    const name = tree.label !== sourceTree.label ? tree.label : initialName;
    const rename = React.useCallback((id: number, label: string) => {
        const trimmed = label.trim();
        if (trimmed) addEdit({type: 'rename', id, label: trimmed});
    }, [addEdit]);
    // Start with the L1 categories visible
    const [expanded, setExpanded] = React.useState<ReadonlySet<number>>(() => new Set([tree.id]));
    const [view, setView] = React.useState<CanvasView>('tree');
    // ECharts needs a number: the same space the tree canvas takes (calc(100vh - 290px), at least 500)
    const treemapHeight = Math.max(500, window.innerHeight - 290);
    const spend = React.useMemo(() => mockTaxonomySpend(tree), [tree]);
    const index = React.useMemo(() => indexTaxonomy(tree), [tree]);
    const [selection, setSelection] = React.useState<LevelSelection>([]);
    // After a merge / move / delete, keep the deepest chosen category that still exists, at its current path
    const selectedPath = React.useMemo(() => {
        // The deepest chosen category (that still exists) at its current place in the tree
        return deepestSelected(index, selection)?.path ?? [];
    }, [selection, index]);
    const focusedId = selectedPath.at(-1);

    const layout = React.useMemo(
        () => layoutTaxonomy(tree, name, expanded, spend),
        [tree, name, expanded, spend]
    );
    const {drag, onNodeDrag, onNodeDragStop} = useTaxonomyDrag(layout.nodes, setPendingDrop);
    const baseNodes = React.useMemo(
        () => layout.nodes.map(n => ({
            ...n,
            data: {
                ...n.data,
                focused: Number(n.id) === focusedId,
                onRename: (label: string) => rename(Number(n.id), label),
                // The root is the taxonomy itself and cannot be deleted
                onDelete: n.data.level === 0 ? undefined : () => setPendingDelete({
                    id: Number(n.id),
                    label: n.data.label,
                    descendantCount: n.data.descendantCount,
                }),
            },
        })),
        [layout, focusedId, rename]
    );
    // While dragging only the dragged card and the drop target get new objects, so React Flow
    // re-renders those two cards instead of all of them on every mouse move
    const nodes = React.useMemo(() => {
        if (!drag) return baseNodes;
        return baseNodes.map(n => {
            if (n.id === drag.id) {
                // The dragged card follows the pointer and stays on top
                return {...n, position: drag.position, zIndex: 1000, data: {...n.data, dragging: true}};
            }
            if (n.id === drag.targetId && drag.action) return {...n, data: {...n.data, dropAction: drag.action}};
            return n;
        });
    }, [baseNodes, drag]);
    // Hovered card, shown in a readable panel while zoomed out (looked up by id so edits can't leave it stale)
    const [hoveredId, setHoveredId] = React.useState<string | null>(null);
    const hoveredNode = hoveredId ? nodes.find(n => n.id === hoveredId) : undefined;
    const hoveredParents = (index.byId.get(Number(hoveredId))?.path ?? []).slice(0, -1)
        .map(id => index.byId.get(id)?.label ?? '');

    // Edges from the root down to the category chosen in the filters are drawn in green
    const edges = React.useMemo(() => {
        if (selectedPath.length === 0) return layout.edges;
        const pathIds = [tree.id, ...selectedPath].map(String);
        const onPath = new Set(pathIds.slice(1).map((id, i) => `${pathIds[i]}-${id}`));
        return layout.edges.map(e => onPath.has(e.id)
            ? {...e, zIndex: 1, style: {stroke: theme.palette.success.main, strokeWidth: 2.5}}
            : e);
    }, [layout.edges, selectedPath, tree.id, theme]);

    // Zoom to the chosen category once it is laid out (it may only appear after expanding its parents).
    // `setCenter` / `fitBounds` move the viewport right away; `fitView` only runs on React Flow's next node
    // update, which never comes when the chosen card was already on screen (e.g. an L1).
    const {setCenter, fitBounds} = useReactFlow();
    const pendingFocus = React.useRef<string | null>(null);
    React.useEffect(() => {
        const node = baseNodes.find(n => n.id === pendingFocus.current);
        if (!node) return;
        pendingFocus.current = null;
        void setCenter(node.position.x + NODE_WIDTH / 2, node.position.y + NODE_HEIGHT / 2, {zoom: 1.1, duration: 600});
    }, [baseNodes, setCenter]);

    const onFilterChange = (next: LevelSelection) => {
        setSelection(next);
        const target = deepestSelected(index, next);
        if (!target) {
            void fitBounds(getNodesBounds(baseNodes), {padding: 0.1, duration: 600});
            return;
        }
        pendingFocus.current = String(target.id);
        // Expand every ancestor so the target is visible
        setExpanded(prev => new Set([...prev, tree.id, ...target.path.slice(0, -1)]));
    };

    const confirmDelete = () => {
        if (!pendingDelete) return;
        addEdit({type: 'delete', id: pendingDelete.id});
        setPendingDelete(null);
    };

    const confirmDrop = (mergedName: string) => {
        if (!pendingDrop) return;
        const {action, source, target} = pendingDrop;
        if (action === 'merge') {
            // One edit: the merged category keeps the target's id, with the name chosen in the dialog
            const name = mergedName !== target.label ? mergedName : undefined;
            addEdit({type: 'merge', sourceId: source.id, targetId: target.id, name});
        } else {
            addEdit({type: 'move', id: source.id, parentId: target.id});
        }
        // Show the result: open the target so the merged / moved categories are visible
        if (action === 'move' || source.childCount > 0) setExpanded(prev => new Set(prev).add(target.id));
        setPendingDrop(null);
    };

    // Handlers passed to React Flow must keep their identity: React Flow hands them to every card,
    // so a new function re-renders all cards (e.g. on every hover)
    const onNodeClick = React.useCallback<NodeMouseHandler<TaxonomyCanvasNode>>((_, node) => {
        if (node.data.childCount === 0) return;
        const id = Number(node.id);
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);
    const onNodeMouseEnter = React.useCallback<NodeMouseHandler<TaxonomyCanvasNode>>((_, n) => setHoveredId(n.id), []);
    const onNodeMouseLeave = React.useCallback(() => setHoveredId(null), []);

    return <>
        <ClickToEdit
            text={name}
            onSave={value => rename(tree.id, value)}
            confirmBeforeSave
            placeholder="Taxonomy name"
            typographyProps={{variant: 'h6', fontWeight: 600}}
            sx={{mb: 1.5, ml: -0.5}}
        />
        <Stack direction="row" alignItems="flex-start" spacing={2}>
            <Box sx={{flex: 1, minWidth: 0}}>
                <TaxonomyLevelFilter index={index} selection={selection} onChange={onFilterChange}/>
            </Box>
            <SegmentedControl
                labels={VIEWS.map(v => v.label)}
                value={VIEWS.findIndex(v => v.key === view)}
                onChange={i => setView(VIEWS[i].key)}
                sx={{mt: 0.5, flexShrink: 0}}
            />
        </Stack>
        {view === 'treemap'
            ? <TaxonomyTreemap tree={tree} name={name} spend={spend} focusId={focusedId} height={treemapHeight}/>
            : <Box sx={{height: 'calc(100vh - 290px)', minHeight: 500, border: 1, borderColor: 'divider', borderRadius: 2}}>
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={NODE_TYPES}
                    onNodeClick={onNodeClick}
                    onNodeMouseEnter={onNodeMouseEnter}
                    onNodeMouseLeave={onNodeMouseLeave}
                    onNodeDrag={onNodeDrag}
                    onNodeDragStop={onNodeDragStop}
                    // A small move is still a click (expand / collapse), not a drag
                    nodeDragThreshold={6}
                    defaultEdgeOptions={{style: {stroke: theme.palette.mithraBlue[300], strokeWidth: 1.5}}}
                    nodesDraggable
                    nodesConnectable={false}
                    elementsSelectable={false}
                    minZoom={0.1}
                    fitView
                    fitViewOptions={{maxZoom: 1}}
                    proOptions={{hideAttribution: true}}
                >
                    <Background variant={BackgroundVariant.Dots} gap={20} size={1}/>
                    <Controls showInteractive={false}/>
                    <MiniMap<TaxonomyCanvasNode>
                        pannable
                        zoomable
                        nodeColor={n => n.data.level === 0 ? theme.palette.primary.main : spendColor(theme, n.data.spendShare)}
                        nodeStrokeColor={theme.palette.mithraBlue[300]}
                    />
                    <Panel position="top-right"><SpendLegend/></Panel>
                    <HoveredNodePanel node={hoveredNode} parents={hoveredParents}/>
                </ReactFlow>
            </Box>}
        <DeleteCategoryDialog request={pendingDelete} onConfirm={confirmDelete} onCancel={() => setPendingDelete(null)}/>
        <TaxonomyDropDialog request={pendingDrop} onConfirm={confirmDrop} onCancel={() => setPendingDrop(null)}/>
    </>
});
